// CardImage.tsx
import React from 'react';
import {
  SYS_PAGE_MARGIN_XS,
  SYS_PAGE_MARGIN_SM,
  SYS_PAGE_MARGIN_MD,
  SYS_PAGE_MARGIN_LG,
  SYS_PAGE_MARGIN_XL,
} from '@oneaudi/unified-web-common';
import styled, { css } from 'styled-components';

const carImageWidths = [320, 420, 560, 680, 820, 960, 1100, 1280];
const CARD_PADDING = 32; // theme.spacing.fixed200 * 2
const GRID_GAP = 16;
const SIDEBAR_FIXED = 320; // fixed left sidebar from 1024px+
const doublePx = (token: string) => `${parseInt(token, 10) * 2}px`;

const sizesStr = [
  `(min-width: 1920px) calc((((100vw - ${doublePx(SYS_PAGE_MARGIN_XL)}) * 3 / 4) - ${GRID_GAP * 3}px) / 4 - ${CARD_PADDING}px)`,
  `(min-width: 1440px) calc((((100vw - ${doublePx(SYS_PAGE_MARGIN_XL)}) * 3 / 4) - ${GRID_GAP * 2}px) / 3 - ${CARD_PADDING}px)`,
  `(min-width: 1280px) calc((((100vw - ${doublePx(SYS_PAGE_MARGIN_LG)}) * 3 / 4) - ${GRID_GAP}px) / 2 - ${CARD_PADDING}px)`,
  `(min-width: 1026px) calc(((100vw - ${doublePx(SYS_PAGE_MARGIN_LG)} - ${SIDEBAR_FIXED}px) - ${GRID_GAP}px) / 2 - ${CARD_PADDING}px)`,
  `(min-width: 1024px) calc(((100vw - ${doublePx(SYS_PAGE_MARGIN_MD)} - ${SIDEBAR_FIXED}px) - ${GRID_GAP}px) / 2 - ${CARD_PADDING}px)`,
  `(min-width: 768px) calc(((100vw - ${doublePx(SYS_PAGE_MARGIN_MD)}) - ${GRID_GAP}px) / 2 - ${CARD_PADDING}px)`,
  `(min-width: 375px) calc(100vw - ${doublePx(SYS_PAGE_MARGIN_SM)} - ${CARD_PADDING}px)`,
  `calc(100vw - ${doublePx(SYS_PAGE_MARGIN_XS)} - ${CARD_PADDING}px)`,
].join(', ');

type ContainerProps = {
  aspectRatio: string;
  className?: string;
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  children: React.ReactNode;
};

const Container: React.FC<ContainerProps> = ({ href, target = '_self', rel, ...rest }) => {
  if (href) {
    const relSafe = target === '_blank' ? 'noopener noreferrer' : rel;
    return <LinkWrapper href={href} target={target} rel={relSafe} {...rest} />;
  }
  return <Wrapper {...rest} />;
};

type CardImageProps = {
  imageUrl?: string;
  alt: string;
  aspectRatio?: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
};

export const CardImage: React.FC<CardImageProps> = ({
  imageUrl,
  alt,
  aspectRatio = '7 / 3',
  className,
  loading = 'lazy',
  href,
  target = '_self',
  rel,
}) => {
  const linkProps = href
    ? {
        href,
        target,
        rel: target === '_blank' ? 'noopener noreferrer' : rel,
      }
    : {};

  if (!imageUrl) {
    // maybe a Fallback image here
    return (
      <Container aspectRatio={aspectRatio} className={className} {...linkProps}>
        <StyledImg alt={alt} aria-hidden="true" />
      </Container>
    );
  }
  const srcSet = carImageWidths
    .map((w) => {
      const u = new URL(imageUrl);
      u.searchParams.set('width', String(w));
      u.searchParams.set('mimetype', 'image/webp');
      return `${u.toString()} ${w}w`;
    })
    .join(', ');

  return (
    <Container aspectRatio={aspectRatio} className={className} {...linkProps}>
      <StyledImg src={imageUrl} srcSet={srcSet} sizes={sizesStr} alt={alt} loading={loading} />
    </Container>
  );
};

const base = css<{ aspectRatio: string }>`
  display: block;
  aspect-ratio: ${({ aspectRatio }) => aspectRatio};
  overflow: hidden;
  padding: 0;
`;

const Wrapper = styled.div<{ aspectRatio: string }>`
  ${base}
`;
const LinkWrapper = styled.a<{ aspectRatio: string }>`
  ${base};
  text-decoration: none;
  color: inherit;
`;

const StyledImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
